import { code39Bars, code128Bars, ean13Bars, interleaved2of5Bars, type Bars } from './barcodes';
import { encodeQr, type QrCode, type QrErrorLevel } from './qr';

// Reading ZPL II as a printer would, to draw it on any printer through its normal driver (and on
// screen): our own labels and the marketplaces' (Trendyol, Hepsiburada) with logos, QR codes,
// Code 39 / EAN-13, rotated fields and white-on-black text. Pure: the drawing is the web app's.

export type ZplRotation = 0 | 90 | 180 | 270;

export interface ZplBlock {
  readonly width: number;
  readonly lines: number;
  readonly spacing: number;
  readonly justify: 'L' | 'C' | 'R' | 'J';
}

interface Placed {
  readonly x: number;
  readonly y: number;
  /** ^FO: (x, y) is the top-left corner; ^FT: the text baseline / the bottom of bars and graphics */
  readonly anchor: 'top' | 'baseline';
  readonly reverse: boolean;
}

export type ZplElement =
  | (Placed & {
      readonly kind: 'text';
      readonly text: string;
      /** '0' (scalable, bold condensed) or a bitmap font letter */
      readonly font: string;
      readonly height: number;
      readonly width: number;
      readonly rotation: ZplRotation;
      readonly block: ZplBlock | null;
    })
  | (Placed & { readonly kind: 'box'; readonly w: number; readonly h: number; readonly thickness: number; readonly white: boolean; readonly rounding: number })
  | (Placed & { readonly kind: 'ellipse'; readonly w: number; readonly h: number; readonly thickness: number; readonly white: boolean })
  | (Placed & { readonly kind: 'diagonal'; readonly w: number; readonly h: number; readonly thickness: number; readonly white: boolean; readonly leaning: 'R' | 'L' })
  | (Placed & {
      readonly kind: 'bars';
      readonly symbology: string;
      readonly bars: Bars;
      readonly height: number;
      readonly rotation: ZplRotation;
      /** human readable line */
      readonly hri: 'none' | 'below' | 'above';
    })
  | (Placed & { readonly kind: 'qr'; readonly code: QrCode; readonly module: number })
  | (Placed & {
      readonly kind: 'graphic';
      /** bytes per row; each bit a dot, most significant first, 1 = black */
      readonly bytesPerRow: number;
      readonly rows: number;
      readonly bitmap: Uint8Array | null;
      /** :Z64: data still compressed (the web app inflates it before drawing) */
      readonly z64: string | null;
      readonly scale: number;
    })
  | (Placed & { readonly kind: 'unsupported'; readonly what: string; readonly w: number; readonly h: number });

export interface ParsedZplLabel {
  /** dots; 8 per millimetre on 203 dpi printers */
  readonly width: number;
  readonly height: number;
  readonly copies: number;
  /** ^POI: the printer turns the whole label upside down */
  readonly inverted: boolean;
  readonly elements: readonly ZplElement[];
  /** commands drawn approximately or not at all, for the person printing */
  readonly warnings: readonly string[];
}

const ROTATIONS: Readonly<Record<string, ZplRotation>> = { N: 0, R: 90, I: 180, B: 270 };

const num = (value: string | undefined, fallback: number) => {
  if (value === undefined || value.trim() === '') return fallback;
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const decodeBase64 = (text: string): Uint8Array => Uint8Array.from(atob(text.replace(/\s/g, '')), (c) => c.charCodeAt(0));

/**
 * ASCII hex graphic data, with ZPL's compression: G–Y repeat the next digit 1–19 times, g–z
 * 20–400 times (counts add up), ',' fills the row with white, '!' with black, ':' repeats the last row.
 */
export function decodeGraphicHex(data: string, bytesPerRow: number, totalBytes: number): Uint8Array {
  const rowDigits = bytesPerRow * 2;
  const out = new Uint8Array(totalBytes);
  let row = '';
  let previous = '0'.repeat(rowDigits);
  let written = 0;
  let count = 0;
  const flush = (text: string) => {
    for (let i = 0; i < bytesPerRow && written < totalBytes; i++) out[written++] = parseInt(text.slice(i * 2, i * 2 + 2) || '00', 16);
    previous = text;
    row = '';
  };
  for (const ch of data) {
    if (written >= totalBytes) break;
    const code = ch.charCodeAt(0);
    if (ch >= 'G' && ch <= 'Y') count += code - 70;
    else if (ch >= 'g' && ch <= 'z') count += (code - 102) * 20;
    else if (ch === ',') flush(row.padEnd(rowDigits, '0'));
    else if (ch === '!') flush(row.padEnd(rowDigits, 'F'));
    else if (ch === ':') flush(previous);
    else if (/[0-9A-Fa-f]/.test(ch)) {
      row += ch.repeat(Math.max(1, count));
      count = 0;
      while (row.length >= rowDigits) {
        const full = row.slice(0, rowDigits);
        const rest = row.slice(rowDigits);
        flush(full);
        row = rest;
      }
    }
  }
  if (row && written < totalBytes) flush(row.padEnd(rowDigits, '0'));
  return out;
}

interface GraphicData {
  readonly bitmap: Uint8Array | null;
  readonly z64: string | null;
}

function graphicData(data: string, bytesPerRow: number, totalBytes: number): GraphicData {
  const trimmed = data.trim();
  if (trimmed.startsWith(':Z64:')) return { bitmap: null, z64: trimmed.slice(5).split(':')[0]! };
  if (trimmed.startsWith(':B64:')) return { bitmap: decodeBase64(trimmed.slice(5).split(':')[0]!), z64: null };
  return { bitmap: decodeGraphicHex(trimmed, bytesPerRow, totalBytes), z64: null };
}

/** Field data with ^FH escapes; under ^CI28 the escaped bytes are UTF-8. */
function unescapeField(data: string, indicator: string | null, utf8: boolean): string {
  if (!indicator) return data;
  const pattern = new RegExp(`(?:\\${indicator}[0-9A-Fa-f]{2})+`, 'g');
  return data.replace(pattern, (run) => {
    const bytes = Uint8Array.from(run.split(indicator).filter(Boolean), (h) => parseInt(h.slice(0, 2), 16));
    return utf8 ? new TextDecoder().decode(bytes) : String.fromCharCode(...bytes);
  });
}

/** Code 128 data may carry subset invocations (">;" start C …) the printer acts on; the text is what matters. */
const code128Text = (data: string) => data.replace(/>[0-9:;<=]/g, '');

interface Command {
  readonly name: string;
  readonly params: string;
}

/** ^XX / ~XX commands; ^A takes its font letter as part of the name (^A0, ^AD, ^A@). */
function tokenize(zpl: string): Command[] {
  const text = zpl.replace(/[\r\n]+/g, '');
  const out: Command[] = [];
  let i = 0;
  while (i < text.length) {
    const start = text.indexOf('^', i);
    const tilde = text.indexOf('~', i);
    const at = start === -1 ? tilde : tilde === -1 ? start : Math.min(start, tilde);
    if (at === -1) break;
    const prefix = text[at]!;
    let name = text.slice(at + 1, at + 3).toUpperCase();
    let paramsStart = at + 3;
    if (prefix === '^' && name[0] === 'A' && name !== 'A@' && /[A-Z0-9]/.test(name[1] ?? '')) {
      name = `A${name[1]}`;
    }
    // parameters and field data run to the next command prefix, as on the printer
    const caret = text.indexOf('^', paramsStart);
    const nextTilde = text.indexOf('~', paramsStart);
    let end = caret === -1 ? nextTilde : nextTilde === -1 ? caret : Math.min(caret, nextTilde);
    if (end === -1) end = text.length;
    out.push({ name: `${prefix}${name}`, params: text.slice(paramsStart, end) });
    i = end;
  }
  return out;
}

/** Labels of a ZPL stream (one per ^XA … ^XZ, ^PQ copies counted, not repeated). */
export function parseZplLabels(zpl: string): ParsedZplLabel[] {
  const labels: ParsedZplLabel[] = [];
  const stored = new Map<string, { bytesPerRow: number; rows: number } & GraphicData>();
  let inLabel = false;
  // label state
  let width = 812;
  let height = 1218;
  let copies = 1;
  let inverted = false;
  let utf8 = false;
  let home = { x: 0, y: 0 };
  let defaultFont = { font: '0', height: 30, width: 30 };
  let defaultRotation: ZplRotation = 0;
  let by = { module: 2, ratio: 3, height: 100 };
  let elements: ZplElement[] = [];
  let warnings = new Set<string>();
  // field state
  let origin: { x: number; y: number; anchor: 'top' | 'baseline' } = { x: 0, y: 0, anchor: 'top' };
  let font: { font: string; height: number; width: number; rotation: ZplRotation } | null = null;
  let block: ZplBlock | null = null;
  let hex: string | null = null;
  let reverse = false;
  let pending: { cmd: string; p: string[] } | null = null;
  let data: string | null = null;

  const resetField = () => {
    font = null;
    block = null;
    hex = null;
    reverse = false;
    pending = null;
    data = null;
  };
  const place = (): Placed => ({ x: home.x + origin.x, y: home.y + origin.y, anchor: origin.anchor, reverse });

  const emit = () => {
    const at = place();
    const cmd = pending?.cmd;
    const p = pending?.p ?? [];
    const text = data === null ? null : unescapeField(data, hex, utf8);
    if (cmd === 'GB') {
      const w = num(p[0], 1);
      const h = num(p[1], 1);
      elements.push({ kind: 'box', ...at, w: Math.max(w, num(p[2], 1)), h: Math.max(h, num(p[2], 1)), thickness: num(p[2], 1), white: (p[3] ?? 'B').toUpperCase() === 'W', rounding: num(p[4], 0) });
    } else if (cmd === 'GC') {
      const d = num(p[0], 3);
      elements.push({ kind: 'ellipse', ...at, w: d, h: d, thickness: num(p[1], 1), white: (p[2] ?? 'B').toUpperCase() === 'W' });
    } else if (cmd === 'GE') {
      elements.push({ kind: 'ellipse', ...at, w: num(p[0], 3), h: num(p[1], 3), thickness: num(p[2], 1), white: (p[3] ?? 'B').toUpperCase() === 'W' });
    } else if (cmd === 'GD') {
      elements.push({ kind: 'diagonal', ...at, w: num(p[0], 3), h: num(p[1], 3), thickness: num(p[2], 1), white: (p[3] ?? 'B').toUpperCase() === 'W', leaning: (p[4] ?? 'R').toUpperCase() === 'L' ? 'L' : 'R' });
    } else if (cmd && text !== null && cmd.startsWith('B')) {
      emitBarcode(at, cmd, p, text);
    } else if (text !== null && text !== '') {
      const f = font ?? { ...defaultFont, rotation: defaultRotation };
      elements.push({ kind: 'text', ...at, text, font: f.font, height: f.height, width: f.width, rotation: f.rotation, block });
    }
    resetField();
  };

  const emitBarcode = (at: Placed, cmd: string, p: string[], text: string) => {
    const rotation = ROTATIONS[(p[0] ?? '').toUpperCase()] ?? defaultRotation;
    try {
      if (cmd === 'BC') {
        const hri = (p[2] ?? 'Y').toUpperCase() === 'N' ? 'none' : (p[3] ?? 'N').toUpperCase() === 'Y' ? 'above' : 'below';
        if (/>8/.test(text)) warnings.add('GS1-128 (FNC1) barkodu düz Code 128 olarak çizildi');
        // ">:" starts in set B: every digit its own symbol, as the printer draws it
        const bars = code128Bars(code128Text(text), by.module, text.startsWith('>:') ? 'B' : undefined);
        elements.push({ kind: 'bars', ...at, symbology: 'code128', bars, height: num(p[1], by.height), rotation, hri });
      } else if (cmd === 'B3') {
        const hri = (p[3] ?? 'Y').toUpperCase() === 'N' ? 'none' : (p[4] ?? 'N').toUpperCase() === 'Y' ? 'above' : 'below';
        elements.push({ kind: 'bars', ...at, symbology: 'code39', bars: code39Bars(text, by.module, by.ratio, (p[1] ?? 'N').toUpperCase() === 'Y'), height: num(p[2], by.height), rotation, hri });
      } else if (cmd === 'BE') {
        const hri = (p[2] ?? 'Y').toUpperCase() === 'N' ? 'none' : (p[3] ?? 'N').toUpperCase() === 'Y' ? 'above' : 'below';
        elements.push({ kind: 'bars', ...at, symbology: 'ean13', bars: ean13Bars(text, by.module), height: num(p[1], by.height), rotation, hri });
      } else if (cmd === 'B2') {
        const hri = (p[2] ?? 'Y').toUpperCase() === 'N' ? 'none' : (p[3] ?? 'N').toUpperCase() === 'Y' ? 'above' : 'below';
        elements.push({ kind: 'bars', ...at, symbology: 'i2of5', bars: interleaved2of5Bars(text, by.module, by.ratio, (p[4] ?? 'N').toUpperCase() === 'Y'), height: num(p[1], by.height), rotation, hri });
      } else if (cmd === 'BQ') {
        // data: <error level><input mode>,<text>, e.g. "QA,https://…" or "MM,N0123…"
        const comma = text.indexOf(',');
        const level = (comma > 0 ? text[0]!.toUpperCase() : 'Q') as QrErrorLevel;
        let payload = comma > 0 ? text.slice(comma + 1) : text;
        // manual input: a data type letter (N, A) or B with a 4-digit byte count comes first
        if (comma > 0 && text[1]?.toUpperCase() === 'M') payload = payload.replace(/^(B\d{4}|[NA])/, '');
        const magnification = Math.min(10, Math.max(1, num(p[2], 2)));
        elements.push({ kind: 'qr', ...at, code: encodeQr(payload, (['L', 'M', 'Q', 'H'] as const).includes(level as 'L') ? level : 'Q'), module: magnification });
      } else {
        const names: Readonly<Record<string, string>> = { BX: 'DataMatrix', B7: 'PDF417', B8: 'EAN-8', BU: 'UPC-A', BD: 'MaxiCode', B0: 'Aztec' };
        const what = names[cmd] ?? `^${cmd}`;
        warnings.add(`${what} barkodu çizilemedi (yer tutucu gösterildi)`);
        const size = num(p[1], 10) * 20;
        elements.push({ kind: 'unsupported', ...at, what, w: Math.min(size, 300), h: Math.min(size, 300) });
      }
    } catch (error) {
      warnings.add(`Barkod okunamadı (${cmd}): ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  for (const { name, params } of tokenize(zpl)) {
    const p = params.split(',');
    if (name === '^XA') {
      inLabel = true;
      width = 812;
      height = 1218;
      copies = 1;
      inverted = false;
      home = { x: 0, y: 0 };
      elements = [];
      warnings = new Set();
      resetField();
      continue;
    }
    if (name === '~DG') {
      // ~DGd:name.GRF,total bytes,bytes per row,data
      const [target, total, perRow, ...rest] = p;
      const bytesPerRow = num(perRow, 1);
      const totalBytes = num(total, 0);
      stored.set((target ?? '').toUpperCase().replace(/^[REBA]:/, ''), { bytesPerRow, rows: Math.ceil(totalBytes / bytesPerRow), ...graphicData(rest.join(','), bytesPerRow, totalBytes) });
      continue;
    }
    if (!inLabel) continue;
    switch (name) {
      case '^XZ':
        labels.push({ width, height, copies, inverted, elements, warnings: [...warnings] });
        inLabel = false;
        break;
      case '^PW':
        width = num(p[0], width);
        break;
      case '^LL':
        height = num(p[0], height);
        break;
      case '^LH':
        home = { x: num(p[0], 0), y: num(p[1], 0) };
        break;
      case '^PQ':
        copies = Math.max(1, Math.min(100, num(p[0], 1)));
        break;
      case '^PO':
        inverted = (p[0] ?? '').toUpperCase() === 'I';
        break;
      case '^CI':
        utf8 = num(p[0], 0) === 28;
        break;
      case '^LR':
        if ((p[0] ?? '').toUpperCase() === 'Y') warnings.add('Ters baskı (^LRY) çizilmedi');
        break;
      case '^FW':
        defaultRotation = ROTATIONS[(p[0] ?? 'N').toUpperCase()] ?? 0;
        break;
      case '^CF': {
        const h = num(p[1], defaultFont.height);
        defaultFont = { font: (p[0] ?? '0').toUpperCase() || '0', height: h, width: num(p[2], h) };
        break;
      }
      case '^FO':
        origin = { x: num(p[0], 0), y: num(p[1], 0), anchor: 'top' };
        break;
      case '^FT':
        origin = { x: num(p[0], origin.x), y: num(p[1], origin.y), anchor: 'baseline' };
        break;
      case '^FB':
        block = { width: num(p[0], 0), lines: Math.max(1, num(p[1], 1)), spacing: num(p[2], 0), justify: (['L', 'C', 'R', 'J'] as const).find((j) => j === (p[3] ?? 'L').toUpperCase()) ?? 'L' };
        break;
      case '^TB': {
        const lineHeight = Math.max(1, font?.height ?? defaultFont.height);
        block = { width: num(p[1], 0), lines: Math.max(1, Math.floor(num(p[2], 0) / lineHeight)), spacing: 0, justify: 'L' };
        break;
      }
      case '^FH':
        hex = params.trim() || '_';
        break;
      case '^FR':
        reverse = true;
        break;
      case '^BY':
        by = { module: Math.max(1, num(p[0], by.module)), ratio: Math.min(3, Math.max(2, num(p[1], by.ratio))), height: num(p[2], by.height) };
        break;
      case '^FD':
      case '^FV':
        data = params;
        break;
      case '^FS':
        emit();
        break;
      case '^GF': {
        // ^GFa,b,c,d,data: compression A (hex), byte count, field count, bytes per row
        const [type, , total, perRow, ...rest] = p;
        const bytesPerRow = Math.max(1, num(perRow, 1));
        const totalBytes = num(total, 0);
        if ((type ?? 'A').toUpperCase() !== 'A') {
          warnings.add('İkili (^GFB/^GFC) grafik çizilemedi');
          break;
        }
        elements.push({ kind: 'graphic', ...place(), bytesPerRow, rows: Math.ceil(totalBytes / bytesPerRow), ...graphicData(rest.join(','), bytesPerRow, totalBytes), scale: 1 });
        break;
      }
      case '^XG':
      case '^IM': {
        const graphic = stored.get((p[0] ?? '').toUpperCase().replace(/^[REBA]:/, ''));
        if (!graphic) {
          warnings.add(`Kayıtlı grafik bulunamadı: ${p[0] ?? ''}`);
          break;
        }
        elements.push({ kind: 'graphic', ...place(), bytesPerRow: graphic.bytesPerRow, rows: graphic.rows, bitmap: graphic.bitmap, z64: graphic.z64, scale: name === '^XG' ? Math.max(1, num(p[1], 1)) : 1 });
        break;
      }
      default:
        if (name.startsWith('^A')) {
          const fontName = name.slice(2) === '@' ? '0' : name.slice(2);
          const h = num(p[1], defaultFont.height);
          font = { font: fontName, height: h, width: num(p[2], fontName === '0' ? h : defaultFont.width), rotation: ROTATIONS[(p[0] ?? '').trim().toUpperCase()] ?? defaultRotation };
        } else if (/^\^(G[BCDE]|B[0-9A-Z])$/.test(name)) {
          pending = { cmd: name.slice(1), p };
        }
        // printer settings (^MM, ^MN, ^PR, ~SD, ^MD, ^JM, ^LT, ^FX …) do not change the drawing
        break;
    }
  }
  return labels;
}
